import React, {useState, useMemo} from 'react';
import { usePaymentsAccount } from '../../../Data/Payments';
import TopBar from '../../../Components/TopBar';
import Page from '../../../Components/Containers/Page';
import Form, { Actions, RegexValidator, RequiredValidator, Select, Switch, SwitchGroup, TextField} from '../../../Components/Form';

const PaymentOptions = () => {
    const {account, updatePaymentOptions} = usePaymentsAccount(true);
    const [cardEnabled, setCardEnabled] = useState(false);

    const data = useMemo(() => {
        if (!account) {
            return null;
        }
        setCardEnabled(account?.paymentMethods?.Card?.enabled ?? false);
        return {
            descriptor: account?.descriptor ?? 'getSoapy',
            invoiceLeadTime: account?.invoiceLeadTime ?? 14,
            acceptTips: account?.acceptTips ?? false,
            paymentMethods: Object.keys(account?.paymentMethods ?? []).reduce((acc, method) => {
                acc[method] = account?.paymentMethods[method].enabled ?? false;
                return acc;
            }, {}),
        }
    }, [account]);

    const isConfigured = (method) => account?.paymentMethods?.[method]?.configured === true;

    const help = (lines) => {
        return lines.filter(Boolean).join('\n')
    }

    const handleSubmit = (data) => {
        return updatePaymentOptions({
            ...data,
            paymentMethods: Object.keys(data.paymentMethods).filter(key => data.paymentMethods[key]),
        })
    }

    return <>
        <TopBar left="BackBtn" title="Payment options" />
        <Page>
            <Form onSubmit={handleSubmit} data={data} waitForData>
                <TextField name="descriptor"
                    label="Payment Descriptor"
                    placeholder="getSoapy"
                    validators={[RequiredValidator, RegexValidator(/^[a-zA-Z][\sa-zA-Z0-9]{6,21}$/)]}
                    help={'This will be shown in your customers\' bank statements.\nIt should be similar to your business name and between 7 and 22 characters.'}
                />

                <Select name="invoiceLeadTime"
                    label="Invoice lead time"
                    validators={[RequiredValidator]}
                    options={
                        [1, 7, 14, 30, 60, 90, 120, 180, 365].map(day => ({
                            label: day === 1 ? '1 day' : day+' days',
                            value: day
                        }))
                    }
                    help={'This is the number of days customers have to pay invoices before they are automatically marked as overdue.'}
                />

                <hr />

                <SwitchGroup name="paymentMethods"
                    label="Payment methods"
                    switches={[
                        {
                            name: 'BACS',
                            label: 'Direct Debit',
                            disabled: !isConfigured('BACS'),
                            help: help([
                                'Automatically debit your customers bank account after each job.',
                                !isConfigured('BACS') && 'Your account needs to complete the setup process before you can enable this option.',
                            ]),
                        },
                        {
                            name: 'Card',
                            label: 'Card',
                            onChange: setCardEnabled,
                            disabled: !isConfigured('Card'),
                            help: help([
                                'Allow your customers to pay by credit or debit card.',
                                !isConfigured('Card') && 'Your account needs to complete the setup process before you can enable this option.',
                            ]),
                        },
                        {
                            name: 'Card Recurring',
                            label: 'Card Recurring',
                            disabled: !cardEnabled || !isConfigured('Card Recurring'),
                            help: help([
                                'Allow your customers to save card details so you can charge them automatically for future jobs.',
                                !isConfigured('Card Recurring') && 'Your account needs to complete the setup process before you can enable this option.',
                                !cardEnabled && 'You need to turn on the Card option to use this.',
                            ]),
                        },
                        {
                            name: 'Mobile',
                            label: 'Mobile',
                            disabled: !cardEnabled || !isConfigured('Mobile'),
                            help: help([
                                'Allow your customers to pay using Apple or Google pay.',
                                !isConfigured('Mobile') && 'Your account needs to complete the setup process before you can enable this option.',
                                !cardEnabled && 'You need to turn on the Card option to use this.',
                            ]),
                        },
                        {
                            name: 'Transfer',
                            label: 'Bank Transfer',
                            disabled: !isConfigured('Transfer'),
                            help: help([
                                'Show your bank details in the payment widget and allow your customers to make the transfer manually.',
                                'You can use bank reconciliation to automatically match bank transfers to the customer\'s balance.',
                            ]),
                        },
                    ]}
                />

                <Switch name="acceptTips"
                    label="Accept tips"
                    innerLabel="Show an option to add tips in the payment widget"
                />

                <Actions submit={{label: 'Save'}} cancel={{label: 'Cancel', back: true}} />
            </Form>
        </Page>
    </>
}

export default PaymentOptions;
